import React from "react";
import { HelmetProvider } from "react-helmet-async";
import { motion } from "framer-motion";
import SEOHead from "../components/common/SEOHead";
import { getSEOData } from "../utils/seoConfig";

import EXEHero from "../components/EXE/EXEHero";
import EXEAbout from "../components/EXE/EXEAbout";
import EXEJoinCall from "../components/EXE/EXEJoinCall";

const Home = () => {
  const seoData = getSEOData("home");

  return (
    <HelmetProvider>
      <div className="exe-bg-pattern min-h-screen pt-20">
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden z-0">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-exe-primary opacity-30 rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                scale: 0.5 + Math.random() * 1.5,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.1, 0.3, 0.1],
                scale: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 2 + Math.random() * 6,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        {/* Matrix-style falling code effect */}
        <div className="absolute left-0 top-0 h-full w-16 opacity-5 z-0">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute left-2 w-1 bg-exe-primary font-mono text-xs"
              style={{
                top: `${i * 3}%`,
                height: `${10 + Math.random() * 20}px`,
              }}
              animate={{
                opacity: [0, 1, 0],
                height: [`${10 + Math.random() * 20}px`, `${5 + Math.random() * 10}px`, `${10 + Math.random() * 20}px`],
              }}
              transition={{
                duration: 1 + Math.random() * 3,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
          ))}
        </div>

        <div className="absolute right-0 top-0 h-full w-16 opacity-5 z-0">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i + 100}
              className="absolute right-2 w-1 bg-exe-primary font-mono text-xs"
              style={{
                top: `${i * 3}%`,
                height: `${10 + Math.random() * 20}px`,
              }}
              animate={{
                opacity: [0, 1, 0],
                height: [`${10 + Math.random() * 20}px`, `${5 + Math.random() * 10}px`, `${10 + Math.random() * 20}px`],
              }}
              transition={{
                duration: 1 + Math.random() * 3,
                repeat: Infinity,
                repeatType: "reverse",
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <SEOHead
          title={seoData.title}
          description={seoData.description}
          image={seoData.image}
          url={seoData.url}
          keywords={seoData.keywords}
        />

        <motion.main
          className="main-content flex flex-col gap-16 py-16 items-center relative z-10"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 20,
            duration: 0.4,
          }}
        >
          {/* Hero Section */}
          <div id="hero" className="w-full">
            <EXEHero />
          </div>

          {/* Animated Divider */}
          <div className="w-full flex justify-center my-8">
            <motion.div
              className="h-px bg-gradient-to-r from-transparent via-exe-primary to-transparent w-3/4"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            />
          </div>

          <div className="flex flex-col gap-20 w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
            
            {/* About EXE Section */}
            <div id="about">
              <EXEAbout />
            </div>

            {/* Join Call-to-Action */}
            <div id="join">
              <EXEJoinCall />
            </div>
          </div>
        </motion.main>

        {/* Bottom Divider */}
        <div className="w-full flex justify-center my-8">
          <motion.div
            className="h-px bg-gradient-to-r from-transparent via-exe-primary to-transparent w-3/4"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6 }}
          />
        </div>
      </div>
    </HelmetProvider>
  );
};

export default Home;
